import { alpha, Theme, createTheme } from '@mui/material/styles';
import { CustomTheme } from './themeTypes';

/**
 * Safely applies alpha transparency to a color with fallback
 */
export const safeAlpha = (color: string | undefined, opacity: number, fallback: string): string => {
  return alpha(color || fallback, opacity);
};

/**
 * Gets a theme value safely with fallback
 */
export const getThemeValue = <T>(
  theme: Theme,
  path: string[],
  fallback: T
): T => {
  let current: any = theme;
  
  for (const key of path) {
    if (current === undefined || current === null) {
      return fallback;
    }
    current = current[key];
  }
  
  return (current !== undefined && current !== null) ? current : fallback;
};

/**
 * Profile background color helper
 */
export const getProfileBackgroundColor = (isSelected: boolean, theme: CustomTheme): string => {
  if (isSelected) {
    return theme.palette.mode === 'dark'
      ? safeAlpha(theme.palette.custom.bannerBg, 0.15, theme.palette.primary.main)
      : safeAlpha(theme.palette.custom.bannerBg, 0.08, theme.palette.primary.main);
  }
  return theme.palette.background.default;
};

/**
 * Profile hover background color helper
 */
export const getProfileHoverBackgroundColor = (isSelected: boolean, theme: CustomTheme): string => {
  return theme.palette.mode === 'dark'
    ? safeAlpha(theme.palette.custom.hoverRowBg, 0.6, theme.palette.background.default)
    : safeAlpha(theme.palette.custom.hoverRowBg, 0.8, theme.palette.background.default);
};

/**
 * Avatar background color helper
 */
export const getAvatarBackgroundColor = (isSelected: boolean, theme: CustomTheme): string => {
  if (isSelected) {
    return theme.palette.mode === 'dark' 
      ? (theme.palette.custom.iconPrimary || theme.palette.info.main)
      : (theme.palette.custom.iconPrimary || theme.palette.primary.main);
  }
  return theme.palette.custom.iconDisabled || theme.palette.action.disabled;
};

/**
 * Navigation item styling helper
 */
export const getNavItemStyles = (isSelected: boolean, theme: CustomTheme) => ({
  backgroundColor: isSelected 
    ? getProfileBackgroundColor(isSelected, theme)
    : 'transparent',
  color: isSelected
    ? (theme.palette.custom.navSelectedText || theme.palette.primary.main)
    : (theme.palette.custom.navText || theme.palette.text.primary),
  '&:hover': {
    backgroundColor: getProfileHoverBackgroundColor(isSelected, theme),
  },
});

/**
 * Button variant styling helper
 */
export const getButtonStyles = (variant: 'contained' | 'outlined' | 'text', theme: CustomTheme) => {
  const baseStyles = {
    borderRadius: '20px',
    transition: 'all 0.2s ease-in-out',
  };

  switch (variant) {
    case 'contained':
      return {
        ...baseStyles,
        backgroundColor: theme.palette?.primary?.main,
        color: theme.palette?.custom?.navSelectedText,
        border: 'none',
        '&:hover': {
          backgroundColor: theme.palette.custom.navHoverBg,
          // backgroundColor: theme.palette?.custom?.navHoverBg,
          // color: theme.palette?.custom?.navSelectedText,
          // boxShadow: `0px 4px 12px ${safeAlpha(
          //   theme.palette?.custom?.shadowColor, 
          //   0.2, 
          //   '#101A36'
          // )}`,
        },
        '&:disabled': {
          backgroundColor: theme.palette?.custom?.disabledBg,
          color: theme.palette?.custom?.disabledText,
          iconColor: theme.palette?.custom?.disabledIcon,
        },
      };
    
    case 'outlined':
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: theme.palette?.primary?.main || '#6495ED',
        border: `1px solid ${theme.palette?.primary?.main || '#6495ED'}`,
        '&:hover': {
          backgroundColor: theme.palette.custom.navHoverBg,
          // backgroundColor: safeAlpha(
          //   theme.palette?.primary?.main, 
          //   0.08, 
          //   '#6495ED'
          //),
          borderColor: theme.palette?.custom?.navHoverBg,
        },
        '&:disabled': {
          borderColor: theme.palette?.custom?.disabledBg,
          color: theme.palette?.custom?.disabledText,
          iconColor: theme.palette?.custom?.disabledIcon,
        },
      };
    
    case 'text':
    default:
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: theme.palette?.text?.primary,
        border: 'none',
        '&:hover': {
          backgroundColor: theme.palette.custom.navHoverBg,
          // backgroundColor: safeAlpha(
          //   theme.palette?.custom?.hoverRowBg, 
          //   0.08, 
          //   theme.palette?.mode === 'dark' ? '#182235' : '#F0F4FF'
          // ),
          // color: theme.palette?.custom?.navSelectedText,
        },
        '&:disabled': {
          color: theme.palette?.custom?.disabledText,
          borderColor: theme.palette?.custom?.disabledBg,
          iconColor: theme.palette?.custom?.disabledIcon,
        },
      };
  }
};

/**
 * Card elevation styling helper
 */
export const getCardStyles = (elevated: boolean, theme: CustomTheme) => ({
  borderRadius: 12,
  backgroundColor: elevated 
    ? (theme.palette.custom.surfaceElevated || theme.palette.background.paper)
    : (theme.palette.custom.cardBackground || theme.palette.background.paper),
  boxShadow: elevated 
    ? `0px 4px 12px ${safeAlpha(
        theme.palette.custom.shadowLight, 
        0.1, 
        theme.palette.mode === 'dark' ? '#000000' : '#000000'
      )}`
    : 'none',
  border: `1px solid ${safeAlpha(
    theme.palette.custom.borderColor, 
    0.08, 
    theme.palette.divider
  )}`,
});

/**
 * Input field styling helper
 */
export const getInputStyles = (theme: CustomTheme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '28px',
    backgroundColor: theme.palette.background.paper,
    '& fieldset': {
      borderColor: theme.palette.custom.inputBorder || theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.custom.inputFocus || theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.custom.inputFocus || theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${safeAlpha(
        theme.palette.custom.focusRing, 
        0.2, 
        theme.palette.primary.main
      )}`,
    },
  },
});

/**
 * Responsive breakpoint helper
 */
export const getResponsiveValue = <T>(
  values: { xs?: T; sm?: T; md?: T; lg?: T; xl?: T },
  defaultValue: T
): T => {
  // This would typically use MUI's useMediaQuery in a component
  // For now, return the default value
  return values.md || values.sm || values.xs || defaultValue;
};


